import { makeAutoObservable, runInAction } from 'mobx';
import { rootContainer, TYPE } from '../../../services/container/root';
import EmailContentService, {
	EMAIL_CATEGORY,
	INotificationEvent,
	ISendTestNotficationData,
} from '../../../services/api/email-content-service';
import { SingleValue } from 'react-select';
import { IOption } from '../../../components/inputs/select-dropdown/select-dropdown';
import { IChangeable, ISaveable, WithOld } from '../../../stores/types';
import { INotificationTemplate } from "../emailContentTypes";
import NotificationTemplateStore from "./notification-template-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";

interface INotificationEventChanges extends Partial<INotificationEvent> {
	_client_id?: number | string;
	setting?: { value: string; label: string };
}

class NotificationEventStore implements ISaveable, IChangeable<INotificationEvent> {

	private _changes: INotificationEventChanges = {};
	private _model: INotificationEvent = {} as INotificationEvent;
	private _selectedLocale?: string;
	private _getTranslation!: (key: string, locale: string) => string;
	private _isSaving: boolean = false;

	private _currentTranslationKeys: string[] = [];

	private readonly _translatableFields: string[] = ['title'];
	/** Sub-stores */
	private _notificationTemplateStore?: NotificationTemplateStore;
	private _isEditEmailAllowed: boolean;

	constructor(model: INotificationEvent = {} as INotificationEvent, isEditEmailAllowed: boolean) {
		this._isEditEmailAllowed = isEditEmailAllowed;
		this._setModel(model);
		makeAutoObservable(this);
	}

	hasChanges(): boolean {
		return !!Object.keys(this._changes).length;
	}

	public getUpdatedModel(): INotificationEvent {
		return { ...this._model, ...this._changes };
	}

	public discardChanges(): void {
		this._changes = {} as INotificationEvent;
	}

	public async save(): Promise<void> {
		const updatedModel: WithOld<INotificationEvent> = this.getUpdatedModel();
        updatedModel.__old = this._model;

		await this.emailContentService.updateEvent(updatedModel);
        delete updatedModel.__old;
		this._setModel(updatedModel);
	}

	isSaving(): boolean {
		return this._isSaving;
	}

	setSelectedLocale(selectedLocale: string) {
		runInAction(() => this._selectedLocale = selectedLocale);
		this._notificationTemplateStore?.setSelectedLocale(selectedLocale);
    }

	setTranstlationMethod(callback: (key: string, locale:string) => string) {
		this._getTranslation = callback;
	}
	private get emailContentService(): EmailContentService {
		return rootContainer.get<EmailContentService>(TYPE.EmailContentService);
	}

	private async _setModel(model: INotificationEvent): Promise<void> {
		this._model = model;
		this._changes = {} as INotificationEvent;
		this._notificationTemplateStore = new NotificationTemplateStore(
			model?.notification,
			this._model.eventId,
			model.scope,
			this._selectedLocale ?? 'en'
		);
	}

	public get notificationTemplatesStore() {
		return this._notificationTemplateStore;
	}

	public get isEditEmailAllowed() {
		return this._isEditEmailAllowed;
	}
	public get eventId() {
		return this._changes._client_id ?? this._model.eventId;
	}

	public get notification() {
		return this._model.notification;
	}

	public get title() {
		return this._changes.title ?? this._model.title;
	}
	public get scope() {
		return this._model.scope;
	}
	public get content() {
		return this._changes.content ?? this._model.content;
	}

	public get description() {
		return this._model.description;
	}

	public get index() {
		return this._changes.index ?? this._model.index;
	}
	public get deleted() {
		return this._changes._deleted ?? false;
	}
	public get setting() {
		return this._changes.setting ?? this._model.setting;
	}

	public get category() {
		return this._changes.category ?? this._model.category;
	}

	public get show()  {
		const capability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.EMAILS);

		switch (this.scope) {
			case 'returngo-credits-email':
				return capability.isInstantCreditsEmailSupported;
			case 'manual-credits-email':
				return capability.isManualCreditsEmailSupported;
			case 'instant-exchange-reminder-first-email':
				return capability.isInstantExchangeReminderFirstEmailSupported;
			case 'instant-exchange-reminder-second-email':
				return capability.isInstantExchangeReminderSecondEmailSupported;
			default:
				return true;
		}
	}

	public setNotification(notification: INotificationTemplate| undefined) {
		this._model.notification = notification;
	}

	public setCategory(value: EMAIL_CATEGORY) {
		if (this.category === value) {
			return;
		}
		this._changes.category = value;
	}
	public setSetting(value: SingleValue<IOption>) {
		if (this.setting?.value === value?.value) {
			return;
		}
		this._changes.setting = value as { value: string; label: string };
	}

	public getTranslationKeys(): string[] {
		const keyList: string[] = [];

		if (this.deleted) {
			return keyList;
		}

		this._translatableFields.forEach((field) => {
			const key = this[field as keyof NotificationEventStore];
			if (key) {
				keyList.push(key.toString());
			}
		});

		/** we need this._currentTranslationKeys property to use it as dependency in react hooks,
		 * so that the hook method will run only when the list was changed */
		const isEqualArrays =
			this._currentTranslationKeys.length === keyList.length &&
			this._currentTranslationKeys.every((k) => !!keyList.find((key) => key === k));
		if (!isEqualArrays) {
			this._currentTranslationKeys = keyList;
		}

		return this._currentTranslationKeys;
	}

	public sendTestNotification(testNotificationData: ISendTestNotficationData): Promise<string|undefined> {
		try {
			return this.emailContentService.sendTestEvent(testNotificationData);
		}
		catch(e) {
			return Promise.reject(undefined);
		}
	}
}

export default NotificationEventStore;
